import { useState } from "react";

import { AlertTriangle, Check, FileCheck2 } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { ConfirmationModal } from "@components/ConfirmationModal";

/**
 * The AI-error attestation wording the doctor accepts on finalise. Versioned so
 * the backend can record WHICH wording was signed off (MDRS-271 §4). Bump the
 * version string whenever the copy below changes.
 */
export const ATTESTATION_VERSION = "2026-08-ai-error-v1";

const CASE_PROCESSING_NOTICE =
  "Documents are still being uploaded or processed for this case. You can " +
  "submit once they've finished.";

const ATTESTATION_TEXT =
  "I understand that Report Studio may provide summaries that could contain " +
  "errors. I confirm that I have reviewed and corrected any content I have " +
  "relied upon, and I accept responsibility for ensuring the final report " +
  "accurately reflects my professional opinion.";

type Props = {
  open: boolean;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  /** Resolves with the accepted attestation version. Must return a Promise so
   *  `submitting` can reset in `.finally()`. */
  onConfirm: (attestationVersion: string) => Promise<void>;
  /** The case's documents are still being uploaded or summarised — Submit stays
   *  disabled until they settle (MDRS-508). The modal can already be open when
   *  an upload starts, so this is checked here too, not just on the header. */
  isCaseProcessing?: boolean;
};

/**
 * "Finalise Report" confirmation (MDRS-271). Replaces the old download-PDF
 * modal for doctors: same completion checklist + AI-error attestation, but the
 * password-protect option is gone (the doctor no longer receives a file) and a
 * lock warning is shown. Finalising locks the case and submits the report to
 * MEDirect for internal Vinci upload.
 */
export const FinaliseReportModal = ({
  open,
  isSubmitting,
  onOpenChange,
  onConfirm,
  isCaseProcessing = false,
}: Props) => {
  const [aiDisclaimerChecked, setAiDisclaimerChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resetState = () => {
    setAiDisclaimerChecked(false);
    setSubmitting(false);
  };

  const busy = submitting || isSubmitting;
  const confirmDisabled = !aiDisclaimerChecked || busy || isCaseProcessing;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(ATTESTATION_VERSION);
      onOpenChange(false);
      resetState();
    } catch {
      // onConfirm surfaces its own error toast; keep the modal open for retry.
      setSubmitting(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  };

  return (
    <ConfirmationModal
      open={open}
      isLoading={busy}
      icon={<FileCheck2 className="size-10 text-primary" />}
      title="Finalise Report"
      description=""
      body={
        <div className="space-y-4 text-left">
          {isCaseProcessing && (
            <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning-50 p-4">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
              <p className="text-sm text-foreground-muted">
                {CASE_PROCESSING_NOTICE}
              </p>
            </div>
          )}

          <p className="text-sm text-foreground-muted">
            Before finalising this report, please confirm you have completed the
            following:
          </p>

          <ul className="space-y-2">
            {[
              "Reviewed all sections of this report thoroughly.",
              "Added my medical opinion to all relevant sections.",
              "Selected the applicable expert witness code(s) of conduct.",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <Check className="mt-0.5 size-4 shrink-0 text-success" />
                <span className="text-sm text-foreground-muted">{item}</span>
              </li>
            ))}
          </ul>

          {/* AI disclaimer — div wrapper because Radix Checkbox renders a
              <button>, which is not a labelable element. */}
          <div className="flex cursor-pointer items-start gap-3 rounded-lg border border-primary/30 bg-primary-50 p-4">
            <Checkbox
              id="finalise-ai-disclaimer"
              checked={aiDisclaimerChecked}
              onCheckedChange={(checked) => setAiDisclaimerChecked(checked === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="finalise-ai-disclaimer"
              className="cursor-pointer text-sm font-normal leading-relaxed"
            >
              {ATTESTATION_TEXT}
            </Label>
          </div>

          {/* Lock warning — finalising is irreversible from the doctor side. */}
          <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning-50 p-4">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
            <p className="text-sm text-foreground-muted">
              Once submitted, this report will be locked. It cannot be edited or
              reopened.
            </p>
          </div>

          {/* The processing notice usually appears while the modal is already
              open (an upload starts mid-review). Screen readers only announce
              changes to a live region that already exists, so this one stays
              mounted and only its text changes. Visually hidden, and last so
              it adds no spacing — sighted users get the notice above. */}
          <p role="status" className="sr-only">
            {isCaseProcessing ? CASE_PROCESSING_NOTICE : ""}
          </p>
        </div>
      }
      confirmButtonText="Submit to MEDirect"
      confirmDisabled={confirmDisabled}
      cancelButtonText="Go back"
      onConfirm={handleConfirm}
      onOpenChange={handleOpenChange}
    />
  );
};
